import { supabase } from '../supabase';

// Define interfaces for metrics and score results
interface ContentScoreBreakdown {
  engagement: number;
  reach: number;
  growth: number;
  keywords: number;
  consistency: number;
}

export interface ContentScoreResult {
  total: number;
  breakdown: ContentScoreBreakdown;
  recommendations: string[];
}

export interface PlatformMetrics {
  followers?: number;
  followers_count?: number;
  likes?: number;
  like_count?: number;
  comments?: number;
  comment_count?: number;
  shares?: number;
  retweet_count?: number;
  share_count?: number;
  impressions?: number;
  impression_count?: number;
  views?: number;
  view_count?: number;
  engagement_rate?: number;
  tweet_count?: number;
  post_count?: number;
  video_count?: number;
}

export class ContentScoreService {
  private userId: string;
  private cache: Map<string, { data: any; timestamp: number }>;
  private CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(userId: string) {
    this.userId = userId;
    this.cache = new Map();
  }

  /**
   * Calculate content score for a specific platform
   */
  async calculatePlatformScore(platform: string): Promise<ContentScoreResult> {
    try {
      // Get connected account for this platform
      const { data: accounts, error: accountError } = await supabase 
        .from('social_accounts')
        .select('*')
        .eq('user_id', this.userId)
        .eq('platform', platform)
        .eq('connected', true);

      if (accountError) {
        console.error(`Error fetching ${platform} account:`, accountError);
        throw new Error(`Error fetching ${platform} account: ${accountError.message}`);
      }

      if (!accounts || accounts.length === 0) {
        // Return default score if no connected accounts
        return this.getDefaultScore(platform);
      }

      // Use the first connected account
      const account = accounts[0];

      // Get historical metrics for comparison
      const { data: historicalMetrics, error: metricsError } = await supabase
        .from('social_metrics')
        .select('*')
        .eq('user_id', this.userId)
        .eq('platform', platform)
        .order('recorded_at', { ascending: false })
        .limit(30);

      if (metricsError) {
        console.error('Error fetching historical metrics:', metricsError);
      }

      // Get current metrics from the account
      const metrics: PlatformMetrics = account.metrics || {};
      
      console.log(`Metrics for ${platform}:`, metrics);
      
      // Calculate score components
      const breakdown = this.calculateScoreBreakdown(metrics, historicalMetrics || []);
      
      // Calculate total score (weighted average)
      const weights = {
        engagement: 0.3,
        reach: 0.25,
        growth: 0.2,
        keywords: 0.15,
        consistency: 0.1
      };

      const total = Math.round(
        breakdown.engagement * weights.engagement +
        breakdown.reach * weights.reach +
        breakdown.growth * weights.growth +
        breakdown.keywords * weights.keywords +
        breakdown.consistency * weights.consistency
      );

      // Generate recommendations
      const recommendations = this.generateRecommendations(breakdown, platform);

      return {
        total,
        breakdown,
        recommendations
      };
    } catch (error) {
      console.error(`Error calculating ${platform} score:`, error);
      throw error;
    }
  }

  /**
   * Get default score when no accounts are connected
   */
  private getDefaultScore(platform: string): ContentScoreResult {
    const breakdown: ContentScoreBreakdown = {
      engagement: 0,
      reach: 0,
      growth: 0,
      keywords: 0,
      consistency: 0
    };
    
    const recommendations = [
      `Connect your ${platform} account to get personalized recommendations`,
      `Set up your ${platform} profile for better performance tracking`,
      `Add content to your ${platform} account to start building engagement`
    ];
    
    return {
      total: 0,
      breakdown,
      recommendations
    };
  }

  /**
   * Calculate overall content score across all platforms
   */
  async calculateOverallScore(): Promise<ContentScoreResult> {
    try {
      // Get all connected accounts
      const { data: accounts, error: accountsError } = await supabase
        .from('social_accounts')
        .select('*')
        .eq('user_id', this.userId)
        .eq('connected', true);

      if (accountsError) {
        throw accountsError;
      }

      if (!accounts || accounts.length === 0) {
        // Return default score if no connected accounts
        return {
          total: 0,
          breakdown: {
            engagement: 0,
            reach: 0,
            growth: 0,
            keywords: 0,
            consistency: 0
          },
          recommendations: [
            'Connect your social media accounts in Settings',
            'Set up your profiles for better performance tracking',
            'Start posting content to build engagement metrics'
          ]
        };
      }

      // Calculate score for each platform
      const platformScores = await Promise.all(
        accounts.map(account => this.calculatePlatformScore(account.platform))
      );

      // Average the scores
      const totalScore = Math.round(
        platformScores.reduce((sum, score) => sum + score.total, 0) / platformScores.length
      );

      // Average the breakdowns
      const breakdown: ContentScoreBreakdown = {
        engagement: 0,
        reach: 0,
        growth: 0,
        keywords: 0,
        consistency: 0
      };

      platformScores.forEach(score => {
        breakdown.engagement += score.breakdown.engagement / platformScores.length;
        breakdown.reach += score.breakdown.reach / platformScores.length;
        breakdown.growth += score.breakdown.growth / platformScores.length;
        breakdown.keywords += score.breakdown.keywords / platformScores.length;
        breakdown.consistency += score.breakdown.consistency / platformScores.length;
      });

      // Get top recommendations across platforms
      const allRecommendations = platformScores.flatMap(score => score.recommendations);
      const uniqueRecommendations = [...new Set(allRecommendations)];
      const topRecommendations = uniqueRecommendations.slice(0, 5);

      return {
        total: totalScore,
        breakdown,
        recommendations: topRecommendations
      };
    } catch (error) {
      console.error('Error calculating overall score:', error);
      throw error;
    }
  }

  /**
   * Calculate content score for a specific caption or text
   */
  async analyzeCaption(caption: string, platform: string): Promise<ContentScoreResult> {
    try {
      // For caption analysis, we'll use a simpler scoring model
      // This could be enhanced with NLP or ML in a production environment
      
      const breakdown: ContentScoreBreakdown = {
        engagement: 0,
        reach: 0,
        growth: 0,
        keywords: 0,
        consistency: 0
      };
      
      // Basic metrics
      const wordCount = caption.split(/\s+/).length;
      const hasHashtags = caption.includes('#');
      const hasMention = caption.includes('@');
      const hasQuestion = caption.includes('?');
      const hasEmoji = /[\p{Emoji}]/u.test(caption);
      const hasUrl = /https?:\/\/[^\s]+/.test(caption);
      
      // Platform-specific scoring
      switch (platform) {
        case 'instagram':
          // Instagram favors longer captions with many hashtags
          breakdown.engagement = this.scoreRange(wordCount, 50, 150, 0, 100);
          breakdown.reach = hasHashtags ? 80 : 30;
          breakdown.keywords = this.calculateKeywordScore(caption, ['instagram', 'insta', 'photo', 'pic']);
          breakdown.consistency = 70; // Placeholder
          breakdown.growth = hasQuestion ? 80 : 60;
          break;
          
        case 'twitter':
          // Twitter favors concise content
          breakdown.engagement = this.scoreRange(wordCount, 10, 30, 0, 100);
          breakdown.reach = hasHashtags ? 70 : 40;
          breakdown.keywords = this.calculateKeywordScore(caption, ['twitter', 'tweet', 'trending']);
          breakdown.consistency = 70; // Placeholder
          breakdown.growth = hasMention ? 80 : 60;
          break;
          
        case 'youtube':
          // YouTube favors detailed descriptions with keywords
          breakdown.engagement = this.scoreRange(wordCount, 100, 300, 0, 100);
          breakdown.reach = hasHashtags ? 60 : 40;
          breakdown.keywords = this.calculateKeywordScore(caption, ['youtube', 'video', 'watch', 'subscribe']);
          breakdown.consistency = 70; // Placeholder
          breakdown.growth = hasUrl ? 80 : 50;
          break;
          
        case 'tiktok':
          // TikTok favors short, trendy content
          breakdown.engagement = this.scoreRange(wordCount, 5, 20, 0, 100);
          breakdown.reach = hasHashtags ? 90 : 30;
          breakdown.keywords = this.calculateKeywordScore(caption, ['tiktok', 'fyp', 'foryou', 'viral']);
          breakdown.consistency = 70; // Placeholder
          breakdown.growth = hasEmoji ? 80 : 50;
          break;
          
        default:
          // Default scoring
          breakdown.engagement = 50;
          breakdown.reach = 50;
          breakdown.keywords = 50;
          breakdown.consistency = 50;
          breakdown.growth = 50;
      }
      
      // Calculate total score
      const weights = {
        engagement: 0.25,
        reach: 0.25,
        growth: 0.15,
        keywords: 0.25,
        consistency: 0.1
      };
      
      const total = Math.round(
        breakdown.engagement * weights.engagement +
        breakdown.reach * weights.reach +
        breakdown.growth * weights.growth +
        breakdown.keywords * weights.keywords +
        breakdown.consistency * weights.consistency
      );
      
      // Generate recommendations
      const recommendations = this.generateCaptionRecommendations(breakdown, platform, {
        wordCount,
        hasHashtags,
        hasMention,
        hasQuestion,
        hasEmoji,
        hasUrl
      });
      
      return {
        total,
        breakdown,
        recommendations
      };
    } catch (error) {
      console.error('Error analyzing caption:', error);
      throw error;
    }
  }

  /**
   * Calculate score breakdown from metrics
   */
  private calculateScoreBreakdown(
    metrics: PlatformMetrics,
    historicalMetrics: any[]
  ): ContentScoreBreakdown {
    // Calculate engagement score - normalize different platform metrics
    let engagementRate = metrics.engagement_rate || 0;
    
    // If no explicit engagement rate, calculate it from available metrics
    if (engagementRate === 0) {
      const followers = metrics.followers || metrics.followers_count || 1; // Prevent division by zero
      const likes = metrics.likes || metrics.like_count || 0;
      const comments = metrics.comments || metrics.comment_count || 0;
      const shares = metrics.shares || metrics.retweet_count || metrics.share_count || 0;
      
      const totalEngagements = likes + comments + shares;
      engagementRate = (totalEngagements / followers) * 100;
    }
    
    const engagement = this.scoreRange(engagementRate, 0, 5, 0, 100);
    
    // Calculate reach score
    const followers = metrics.followers || metrics.followers_count || 1; // Prevent division by zero
    const impressions = metrics.impressions || metrics.impression_count || metrics.views || metrics.view_count || 0;
    const impressionsPerFollower = impressions / followers;
    const reach = this.scoreRange(impressionsPerFollower, 0, 3, 0, 100);
    
    // Calculate growth score
    let growth = 50; // Default
    if (historicalMetrics.length > 0) {
      const oldestMetric = historicalMetrics[historicalMetrics.length - 1];
      const oldFollowers = oldestMetric.total_followers || 0;
      const currentFollowers = metrics.followers || metrics.followers_count || 0;
      
      if (oldFollowers > 0) {
        const growthRate = (currentFollowers - oldFollowers) / oldFollowers;
        growth = this.scoreRange(growthRate * 100, 0, 10, 0, 100);
      }
    }
    
    // Placeholder scores for metrics we can't directly calculate
    // For keywords, check if we have content-related metrics
    let keywords = 70;
    const postCount = metrics.tweet_count || metrics.post_count || metrics.video_count || 0;
    if (postCount > 0) {
      // More posts generally means better keyword optimization
      keywords = this.scoreRange(postCount, 0, 100, 50, 90);
    }
    
    // For consistency, check historical data frequency
    let consistency = 50;
    if (historicalMetrics.length > 0) {
      consistency = this.scoreRange(historicalMetrics.length, 0, 30, 40, 100);
    }
    
    return {
      engagement,
      reach,
      growth,
      keywords,
      consistency
    };
  }

  /**
   * Calculate keyword score based on presence of important keywords
   */
  private calculateKeywordScore(text: string, importantKeywords: string[]): number {
    const lowerText = text.toLowerCase();
    const keywordCount = importantKeywords.filter(keyword => 
      lowerText.includes(keyword.toLowerCase())
    ).length;
    
    return Math.min(100, Math.round((keywordCount / importantKeywords.length) * 100) + 50);
  }

  /**
   * Score a value within a range
   */
  private scoreRange(value: number, min: number, max: number, outMin: number, outMax: number): number {
    if (value <= min) return outMin;
    if (value >= max) return outMax;
    
    const percentage = (value - min) / (max - min);
    return Math.round(outMin + percentage * (outMax - outMin));
  }

  /**
   * Generate recommendations based on score breakdown
   */
  private generateRecommendations(breakdown: ContentScoreBreakdown, platform: string): string[] {
    const recommendations: string[] = [];
    
    // Platform-specific recommendations
    const platformSpecific: Record<string, string[]> = {
      twitter: [
        "Use trending hashtags to increase visibility",
        "Engage in relevant Twitter conversations",
        "Post during high-activity hours (9 AM - 3 PM)"
      ],
      facebook: [
        "Share more video content for higher engagement",
        "Create engaging polls and questions",
        "Utilize Facebook Stories for increased reach"
      ],
      instagram: [
        "Post consistently to Instagram Stories",
        "Use relevant hashtags (up to 30)",
        "Create more Reels for better reach"
      ],
      youtube: [
        "Optimize video titles and thumbnails",
        "Create longer content (8-12 minutes)",
        "Add end screens and cards to videos"
      ],
      linkedin: [
        "Share industry insights and expertise",
        "Post during business hours",
        "Engage with industry leaders' content"
      ]
    };
    
    // Add platform-specific recommendations
    if (platformSpecific[platform]) {
      recommendations.push(...platformSpecific[platform]);
    }
    
    // Engagement recommendations
    if (breakdown.engagement < 60) {
      recommendations.push(`Improve engagement on ${platform} by asking questions and encouraging responses`);
      recommendations.push(`Create more interactive content on ${platform} to boost engagement rates`);
    }
    
    // Reach recommendations
    if (breakdown.reach < 60) {
      recommendations.push(`Optimize your ${platform} content for better reach by using relevant hashtags`);
      recommendations.push(`Post during peak hours to maximize visibility on ${platform}`);
    }
    
    // Growth recommendations
    if (breakdown.growth < 60) {
      recommendations.push(`Increase posting frequency on ${platform} to accelerate follower growth`);
      recommendations.push(`Collaborate with other creators to expand your ${platform} audience`);
    }
    
    // Keywords recommendations
    if (breakdown.keywords < 60) {
      recommendations.push(`Research and use more trending keywords in your ${platform} content`);
      recommendations.push(`Include industry-specific terms in your ${platform} posts to improve discoverability`);
    }
    
    // Consistency recommendations
    if (breakdown.consistency < 70) {
      recommendations.push(`Maintain a consistent posting schedule on ${platform} for better algorithm performance`);
      recommendations.push(`Create a content calendar for ${platform} to ensure regular posting`);
    }
    
    // Return a subset of recommendations to avoid overwhelming the user
    return recommendations.slice(0, 5);
  }

  /**
   * Generate caption-specific recommendations
   */
  private generateCaptionRecommendations(
    breakdown: ContentScoreBreakdown, 
    platform: string,
    metrics: {
      wordCount: number;
      hasHashtags: boolean;
      hasMention: boolean;
      hasQuestion: boolean;
      hasEmoji: boolean;
      hasUrl: boolean;
    }
  ): string[] {
    const recommendations: string[] = [];
    
    // Platform-specific recommendations
    switch (platform) {
      case 'instagram':
        if (!metrics.hasHashtags) {
          recommendations.push('Add relevant hashtags (aim for 20-30) to increase discoverability');
        }
        if (metrics.wordCount < 50) {
          recommendations.push('Lengthen your caption to tell a more engaging story');
        }
        if (!metrics.hasQuestion) {
          recommendations.push('Include a question to encourage comments and boost engagement');
        }
        break;
        
      case 'twitter':
        if (metrics.wordCount > 50) {
          recommendations.push('Shorten your tweet for better engagement');
        }
        if (!metrics.hasHashtags) {
          recommendations.push('Add 1-2 relevant hashtags to increase reach');
        }
        if (!metrics.hasMention) {
          recommendations.push('Consider tagging relevant accounts to expand your reach');
        }
        break;
        
      case 'youtube':
        if (metrics.wordCount < 100) {
          recommendations.push('Add more detail to your description for better SEO');
        }
        if (!metrics.hasUrl) {
          recommendations.push('Include relevant links in your description');
        }
        if (!metrics.hasHashtags) {
          recommendations.push('Add 3-5 relevant hashtags to improve discoverability');
        }
        break;
        
      case 'tiktok':
        if (!metrics.hasHashtags) {
          recommendations.push('Add trending hashtags like #fyp to boost visibility');
        }
        if (!metrics.hasEmoji) {
          recommendations.push('Include emojis to make your caption more engaging');
        }
        if (metrics.wordCount > 30) {
          recommendations.push('Keep your caption concise for better engagement');
        }
        break;
    }
    
    // General recommendations
    if (breakdown.keywords < 70) {
      recommendations.push('Include more relevant keywords related to your content topic');
    }
    
    if (breakdown.engagement < 70) {
      recommendations.push('Add a clear call-to-action to encourage user interaction');
    }
    
    return recommendations;
  }

  /**
   * Analyze a caption for a specific platform using connected account data
   */
  async analyzeCaptionWithAccountData(caption: string, platform: string): Promise<{
    score: ContentScoreResult;
    accountMetrics: PlatformMetrics | null;
  }> {
    // First, get the basic caption analysis
    const basicScore = await this.analyzeCaption(caption, platform);
    
    // Try to get connected account data to enhance the analysis
    const { data: accounts, error: accountError } = await supabase
      .from('social_accounts')
      .select('*') 
      .eq('user_id', this.userId)
      .eq('platform', platform)
      .eq('connected', true);

    if (accountError || !accounts || accounts.length === 0) {
      // No connected account, return basic analysis
      return {
        score: basicScore,
        accountMetrics: null
      };
    }

    // Use account metrics to enhance the analysis
    const account = accounts[0];
    const metrics = account.metrics || {};
    
    // Adjust scores based on account data if available
    if (Object.keys(metrics).length > 0) {
      // Enhance keyword score based on account performance
      if (metrics.engagement_rate) {
        // If account has good engagement, slightly boost the keyword score
        basicScore.breakdown.keywords = Math.min(
          100, 
          basicScore.breakdown.keywords + (metrics.engagement_rate > 3 ? 10 : 0)
        );
      }
      
      // Recalculate total score
      const weights = {
        engagement: 0.25,
        reach: 0.25,
        growth: 0.15,
        keywords: 0.25,
        consistency: 0.1
      };
      
      basicScore.total = Math.round(
        basicScore.breakdown.engagement * weights.engagement +
        basicScore.breakdown.reach * weights.reach +
        basicScore.breakdown.growth * weights.growth +
        basicScore.breakdown.keywords * weights.keywords +
        basicScore.breakdown.consistency * weights.consistency
      );
    }
    
    return {
      score: basicScore,
      accountMetrics: metrics
    };
  }
}